import React from 'react';
import { Shield, Users, Award, FileCheck } from 'lucide-react';
import { governanceData } from '../mock/mockData';

const Governance = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#081221] to-[#0d63ce]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">
            Governance
          </h1>
          <p className="text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Trasparenza, integrità e conformità ai più elevati standard di corporate governance
          </p>
        </div>
      </section>

      {/* Governance Principles */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Principi di Governance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              I valori che guidano la nostra organizzazione
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Integrità',
                description: 'Conduciamo il business con etica e trasparenza'
              },
              {
                icon: Users,
                title: 'Responsabilità',
                description: 'Accountability chiara verso tutti gli stakeholder'
              },
              {
                icon: Award,
                title: 'Eccellenza',
                description: 'Commitment alla qualità e al miglioramento continuo'
              },
              {
                icon: FileCheck,
                title: 'Conformità',
                description: 'Aderenza alle normative e agli standard internazionali'
              }
            ].map((principle, index) => {
              const IconComponent = principle.icon;
              return (
                <div
                  key={index}
                  className="text-center p-8 bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                >
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#081221] mb-4">
                    {principle.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ownership Structure */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Struttura Proprietaria
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Assetto azionario 2026
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-12 shadow-xl">
              <div className="space-y-8">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-semibold text-[#081221]">
                      Terni Grandi Impianti s.r.l.
                    </span>
                    <span className="text-2xl font-bold text-[#0d63ce]">100%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0d63ce] to-[#18a06b] rounded-full transition-all"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Board of Directors */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Consiglio di Amministrazione
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un team di professionisti con competenze complementari
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {governanceData?.board?.map((member, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#081221] text-center mb-2">
                  {member.name}
                </h3>
                <div className="text-[#0d63ce] font-semibold text-center">
                  {member.role}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-[#081221] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold mb-6">Certificazioni</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Standard internazionali di qualità e sicurezza
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {governanceData?.certifications?.map((cert, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="text-white" size={32} />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-bold mb-2">{cert}</h3>
                    <p className="text-white/70">Certificazione attiva e monitorata annualmente</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code of Ethics */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
                Codice Etico
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Il nostro Codice Etico definisce i principi e i valori che guidano il comportamento
                di tutti i membri dell'organizzazione.
              </p>
              <ul className="space-y-4">
                {[
                  'Rispetto delle leggi e dei regolamenti',
                  'Integrità e trasparenza nelle relazioni commerciali',
                  'Tutela della salute e sicurezza sul lavoro',
                  'Rispetto dei diritti umani e del lavoro',
                  'Protezione dell’ambiente',
                  'Prevenzione della corruzione'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#18a06b] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FileCheck className="text-white" size={14} />
                    </div>
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="/codice_etico_book.png"
                alt="Codice Etico e Governance"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Governance;
